import { useState } from "react";

import { useStatsigClient } from "@statsig/react-bindings";

import {
  NEST_PROCESSING_FEE_EXPERIMENT,
  NEST_PROCESSING_FEE_PARAM,
} from "../experiment.consts";

type UseNestProcessingFeeExperimentParams = {
  isDiy: boolean;
  hasQuote: boolean;
};

/**
 * Reads the `nest_processing_fee_test_v2` Statsig experiment for DIY listings
 * once a quote has resolved. Uses `useStatsigClient().getExperiment` rather
 * than the `useExperiment` hook so the eligibility check (`isDiy && hasQuote`)
 * can gate the call itself — ineligible guests never call `getExperiment`, so
 * they aren't exposure-logged/enrolled.
 *
 * Resolves the treatment once, the first eligible render where
 * `useStatsigClient().isLoading` is `false` (initial bootstrap has settled),
 * and then holds onto it in state for the component's lifetime instead of
 * re-deriving `shouldNestFees` from `isLoading` on every render. `isLoading`
 * isn't only true during that initial bootstrap: `StatsigUserProvider`'s
 * `setAttributes` (the single writer for Statsig's `custom` targeting attrs)
 * calls the SDK's `updateUserAsync`, which flips the client back to a
 * `Loading` status — and therefore `isLoading` back to `true` — for the
 * duration of that call. `setAttributes` isn't only called here: e.g.
 * `useZeroDownPayment` fires its own fire-and-forget
 * `setAttributes({ payLaterBucket })` whenever `quote` changes, right after
 * this price summary first renders with the treatment on. Deriving
 * `shouldNestFees` straight from `isLoading` flips it back to `false` for a
 * render whenever that unrelated sync starts (control view flashes back in)
 * until it clears — holding onto the once-resolved value avoids that
 * regardless of how many more times `isLoading` toggles afterward.
 *
 * Resolving via a conditional `setState` call during render (rather than a
 * `useEffect`) is React's documented escape hatch for storing information
 * from previous renders: https://react.dev/reference/react/useState#storing-information-from-previous-renders.
 * It also avoids the extra render `useEffect` would add before
 * `shouldNestFees` first turns on.
 */
export const useNestProcessingFeeExperiment = ({
  isDiy,
  hasQuote,
}: UseNestProcessingFeeExperimentParams) => {
  const { getExperiment, isLoading } = useStatsigClient();
  const isEligible = isDiy && hasQuote;
  const [resolved, setResolved] = useState<boolean | null>(null);

  if (resolved === null && isEligible && !isLoading) {
    setResolved(
      getExperiment(NEST_PROCESSING_FEE_EXPERIMENT).get(
        NEST_PROCESSING_FEE_PARAM,
        false,
      ),
    );
  }

  return { shouldNestFees: isEligible && (resolved ?? false) };
};
